import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Inject, Injectable } from '../../injection';
import { AdminDomainMembership, AdminDomainMembershipEagerLoad } from '../data/admin-domain-membership.do';
import { AdminDomain } from '../data/admin-domain.do';
import { EmptyResponse } from '../data/empty-response.do';
import { ErpCredentials } from '../data/erp-credentials.do';
import { MobizEndpoint } from '../data/mobiz-endpoint.do';
import { PagedResults } from '../data/paged-results.model';
import { ApiEndpointConfiguration } from '../interfaces/api-endpoint-configuration.interface';
import { IMobizHttpService } from '../interfaces/mobiz-http.interface';
import { FlagsEnum } from '../other/flags-enum';
import { MobizServerApi } from '../other/mobiz-server-api.enum';
import {
    AdminDomainMembershipQueryParameters,
    AdminDomainQueryParameters,
    EndpointQueryParameters,
    IAdminDomainService,
} from './admin-domain.interface';

@Injectable()
export class AdminDomainService implements IAdminDomainService {
  constructor(
    @Inject(IMobizHttpService) private http: IMobizHttpService) {
  }

  public createAdminDomain(adminDomain: AdminDomain): Observable<AdminDomain> {
    let config: ApiEndpointConfiguration = {
      serverApi: MobizServerApi.AllAdminDomains,
      body: JSON.stringify(adminDomain),
    };
    return this.http.post(new AdminDomain(), config);
  }

  public getAdminDomain(adminDomainId: string): Observable<AdminDomain> {
    let config: ApiEndpointConfiguration = {
      serverApi: MobizServerApi.AdminDomain,
      urlParameters: {
        adminDomainId: adminDomainId,
      },
    };
    return this.http.get(new AdminDomain(), config);
  }

  public getAdminDomains(queryParameters: AdminDomainQueryParameters): Observable<PagedResults<AdminDomain>> {
    let config: ApiEndpointConfiguration = {
      serverApi: MobizServerApi.AllAdminDomains,
      queryParameters: queryParameters
    };
    let results = new PagedResults<AdminDomain>(AdminDomain);
    return this.http.get(results, config).pipe(map((response: PagedResults<AdminDomain>) => {
      return response;
    }));
  }

  public updateAdminDomain(adminDomain: AdminDomain): Observable<AdminDomain> {
    let config: ApiEndpointConfiguration = {
      body: JSON.stringify(adminDomain),
      serverApi: MobizServerApi.AdminDomain,
      urlParameters: {
        adminDomainId: adminDomain.id,
      },
    };
    return this.http.put(new AdminDomain(), config);
  }

  public deleteAdminDomain(adminDomainId: string): Observable<EmptyResponse> {
    let config: ApiEndpointConfiguration = {
      serverApi: MobizServerApi.AdminDomain,
      urlParameters: {
        adminDomainId: adminDomainId,
      },
    };
    return this.http.delete(new EmptyResponse(), config);
  }

  public createEndpoint(endpoint: MobizEndpoint, credentials: ErpCredentials): Observable<MobizEndpoint> {
    let config: ApiEndpointConfiguration = {
      serverApi: MobizServerApi.AllAdminDomainEndpoints,
      body: JSON.stringify({
        endpoint: endpoint,
        credentials: credentials,
      }),
      urlParameters: {
        adminDomainId: endpoint.adminDomainId,
      },
    };
    return this.http.post(new MobizEndpoint(), config);
  }

  public getEndpoints(queryParameters: EndpointQueryParameters): Observable<PagedResults<MobizEndpoint>> {
    let config: ApiEndpointConfiguration = {
      serverApi: MobizServerApi.AllAdminDomainEndpoints,
      queryParameters: queryParameters,
      urlParameters: {
        adminDomainId: queryParameters.adminDomainId,
      },
    };
    let results = new PagedResults<MobizEndpoint>(MobizEndpoint);
    return this.http.get(results, config).pipe(map((response: PagedResults<MobizEndpoint>) => {
      return response;
    }));
  }

  public updateEndpoint(endpoint: MobizEndpoint, credentials: ErpCredentials): Observable<MobizEndpoint> {
    let config: ApiEndpointConfiguration = {
      serverApi: MobizServerApi.AdminDomainEndpoint,
      body: JSON.stringify({
        endpoint: endpoint,
        credentials: credentials,
      }),
      urlParameters: {
        adminDomainId: endpoint.adminDomainId,
        endpointId: endpoint.id,
      },
    };
    return this.http.put(new MobizEndpoint(), config);
  }

  public deleteEndpoint(adminDomainId: string, endpointId: string): Observable<EmptyResponse> {
    let config: ApiEndpointConfiguration = {
      serverApi: MobizServerApi.AdminDomainEndpoint,
      urlParameters: {
        adminDomainId: adminDomainId,
        endpointId: endpointId,
      },
    };
    return this.http.delete(new EmptyResponse(), config);
  }

  public getAdminDomainMemberships(
    queryParameters: AdminDomainMembershipQueryParameters,
    eagerLoad?: FlagsEnum<AdminDomainMembershipEagerLoad>
  ): Observable<PagedResults<AdminDomainMembership>> {
    let reqQueryParameters = queryParameters;

    if (eagerLoad) {
      reqQueryParameters = { ...queryParameters, eagerLoad: eagerLoad.value };
    }

    let config: ApiEndpointConfiguration = {
      serverApi: MobizServerApi.AllAdminDomainMemberships,
      queryParameters: reqQueryParameters,
      urlParameters: {
        adminDomainId: queryParameters.adminDomainId,
      },
    };
    let pagedMemberships = new PagedResults<AdminDomainMembership>(AdminDomainMembership);
    return this.http.get(pagedMemberships, config).pipe(map((response: PagedResults<AdminDomainMembership>) => {
      return response;
    }));

  }

  public getAdminDomainMembership(adminDomainId: string, membershipId: string): Observable<AdminDomainMembership> {
    let config: ApiEndpointConfiguration = {
      serverApi: MobizServerApi.AdminDomainMembership,
      urlParameters: {
        adminDomainId: adminDomainId,
        membershipId: membershipId,
      },
    };
    return this.http.get(new AdminDomainMembership(), config);
  }

  public createAdminDomainMembership(membership: AdminDomainMembership): Observable<AdminDomainMembership> {
    let config: ApiEndpointConfiguration = {
      serverApi: MobizServerApi.AllAdminDomainMemberships,
      body: JSON.stringify(membership),
      urlParameters: {
        adminDomainId: membership.adminDomainId,
      },
    };
    return this.http.post(new AdminDomainMembership(), config);
  }

  public updateAdminDomainMembership(membership: AdminDomainMembership): Observable<AdminDomainMembership> {
    let config: ApiEndpointConfiguration = {
      serverApi: MobizServerApi.AdminDomainMembership,
      body: JSON.stringify(membership),
      urlParameters: {
        adminDomainId: membership.adminDomainId,
        membershipId: membership.id,
      },
    };
    return this.http.put(new AdminDomainMembership(), config);
  }

  public deleteAdminDomainMembership(adminDomainId: string, membershipId: string): Observable<EmptyResponse> {
    let config: ApiEndpointConfiguration = {
      serverApi: MobizServerApi.AdminDomainMembership,
      urlParameters: {
        adminDomainId: adminDomainId,
        membershipId: membershipId,
      },
    };
    return this.http.delete(new EmptyResponse(), config);
  }

}
